import { runtimeConfig } from '@/lib/config';

import { getAuthTransport } from './authTransport';

/**
 * 백엔드 API 클라이언트.
 *
 * Next.js BFF 가 없어졌으므로 브라우저가 백엔드를 직접 호출한다. 인증(쿠키 전송·CSRF·Bearer 등)은
 * 이 파일이 알지 않는다 — {@link getAuthTransport} 가 돌려주는 AuthTransport 가 헤더와 credentials 를
 * 정한다. 웹은 세션 쿠키 방식, 추후 앱은 토큰 방식을 끼워 넣으므로, 여기 본문은 방식에 중립이다.
 * (배경: `lib/api/authTransport.ts`, docs/04_2026-07-28_cross-platform-mobile-design.md)
 */

export type ApiError = {
  code: string;
  message: string;
  detail?: unknown;
};

type ApiEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: ApiError;
};

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly apiError: ApiError | null,
  ) {
    super(apiError?.message ?? `요청이 실패했습니다. (HTTP ${status})`);
    this.name = 'ApiRequestError';
  }
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * 덧붙일 머리글. <b>인증·CSRF 머리글은 여기로 넣지 않는다</b> — 그것들은 아래에서 통로가
   * 붙이고, 여기 값은 그 뒤에 얹히지 않는다(덮어쓰기로 인증이 깨지지 않게 순서를 고정한다).
   *
   * <p>지금 쓰는 곳은 프로브 조회의 {@code X-Probe-Key} 하나다(DOC-109 §4) — 더 열리게 하는
   * 값이 아니라 그 요청을 기록에 남게 하는 표시다.
   */
  headers?: Record<string, string>;
};

/**
 * 파일 하나를 그대로 받는다(PDF·이미지).
 *
 * <p>{@link apiFetch} 를 쓸 수 없는 이유는 응답이 <b>JSON 봉투가 아니라 바이트</b>이기 때문이다.
 * 대신 인증·재시도는 같은 통로를 지나야 한다 — 여기만 쿠키를 안 붙이면 미리보기가 401 로 죽는다.
 *
 * <p>실패하면 서버가 주는 JSON 오류를 그대로 던진다. 「명함 양식이 없습니다」 같은 사유가
 * 화면에 그대로 보여야, 사람이 다음에 무엇을 할지 안다.
 */
export async function apiFetchBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const { method = 'GET', body, signal } = options;
  const transport = getAuthTransport();
  const unsafe = UNSAFE_METHODS.has(method);

  if (unsafe && transport.ensureReady) {
    await transport.ensureReady(runtimeConfig().apiBaseUrl);
  }

  const send = () =>
    fetch(`${runtimeConfig().apiBaseUrl}${path}`, {
      method,
      headers: {
        Accept: 'application/pdf, application/octet-stream, application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...transport.authHeaders({ unsafe }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      credentials: transport.credentials,
    });

  let response = await send();

  /*
    ★ 401 이면 되살려 한 번 다시 보낸다 — 위 {@link apiFetch} 와 <b>같은 규칙</b>이다.

    예전에는 이쪽만 이 분기가 없었다. 쿠키 방식(웹)은 `onUnauthorized` 를 두지 않아 드러나지
    않지만, <b>토큰 방식(앱)</b>은 액세스 토큰이 만료된 순간 PDF 미리보기·명함 미리보기가
    되살리기 없이 그냥 던졌다. 같은 통로를 쓰는데 한쪽만 다르게 구는 자리였다.
  */
  if (response.status === 401 && transport.onUnauthorized) {
    const recovered = await transport.onUnauthorized();
    if (recovered) response = await send();
  }

  if (!response.ok) {
    const envelope = (await response.json().catch(() => null)) as ApiEnvelope<unknown> | null;
    throw new ApiRequestError(response.status, envelope?.error ?? null);
  }
  return response.blob();
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers: extraHeaders } = options;
  const unsafe = UNSAFE_METHODS.has(method);
  const transport = getAuthTransport();

  // FormData 는 브라우저가 boundary 를 붙여 Content-Type 을 스스로 정한다. 여기서 지정하면
  // boundary 가 빠져 서버가 파트를 못 읽는다 — 그래서 헤더를 건드리지 않고 body 도 그대로 보낸다.
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;

  const buildHeaders = (): Record<string, string> => {
    // 웹인가 앱인가(백엔드 `ClientKind`). 권한이 아니라 <b>그릴 수 있는가</b>를 말하는 값이라,
    // 서버는 이것을 보고 «앱에는 못 그리는 화면으로 답해야 하는 것» 을 목록에서 뺀다.
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Bomi-Client': runtimeConfig().client,
      ...extraHeaders,
    };
    if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
    // 인증 머리글을 **맨 뒤에** 얹는다 — 호출자가 같은 이름을 넣어도 인증이 덮이지 않는다.
    return { ...headers, ...transport.authHeaders({ unsafe }) };
  };

  // 쓰기 요청은 보내기 전에 인증 방식이 준비를 마치게 한다(쿠키 방식: CSRF 토큰 쿠키 확보).
  if (unsafe && transport.ensureReady) {
    await transport.ensureReady(runtimeConfig().apiBaseUrl);
  }

  const send = () =>
    fetch(`${runtimeConfig().apiBaseUrl}${path}`, {
      method,
      headers: buildHeaders(),
      body: body === undefined ? undefined : multipart ? (body as FormData) : JSON.stringify(body),
      signal,
      credentials: transport.credentials,
    });

  let response = await send();

  // 401 이면 인증 방식에게 되살릴 기회를 한 번 준다(토큰 방식의 refresh). 되살아나면 원 요청을 한 번 재시도한다.
  // 쿠키 방식은 onUnauthorized 를 두지 않으므로 여기 분기는 그냥 지나간다.
  if (response.status === 401 && transport.onUnauthorized) {
    const recovered = await transport.onUnauthorized();
    if (recovered) response = await send();
  }

  const envelope = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;

  if (!response.ok || !envelope?.ok) {
    throw new ApiRequestError(response.status, envelope?.error ?? null);
  }

  return envelope.data as T;
}
